import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Devices, Sparkle, ChatCircle } from '@phosphor-icons/react';
import { useWebpages } from '@/contexts/WebpagesContext';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { supabase } from '@/lib/supabase';
import { RouteLoader } from '@/components/RouteLoader';
import { ChordProgress, type ChordPhase } from './components/ChordProgress';
import { SuccessAnimation } from './components/SuccessAnimation';
import { analytics } from '@/lib/analytics';
import { consumePendingDestination } from '@/lib/auth-next';
import { cn } from '@/lib/utils';

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export function OnboardingWelcome() {
  const { user, isAuthLoading } = useWebpages();
  const { status, loading: statusLoading, refetch } = useOnboardingStatus(!!user);
  const [welcomePhase, setWelcomePhase] = useState<ChordPhase>('idle');
  // Floating "add a note, then Save" hint that appears 1s AFTER the user
  // triggers ⌘⇧S — sticky once shown until the save lands, so a slow
  // typer who lingers in the extension's note-input toast still sees the
  // guidance the whole time they need it.
  const [noteHintVisible, setNoteHintVisible] = useState(false);
  const noteHintShownRef = useRef(false);
  const [modDown, setModDown] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const [sDown, setSDown] = useState(false);
  const navigatedRef = useRef(false);
  // Analytics: once-per-mount latches. Also cache the welcome-step mount
  // instant so we can compute step duration_ms.
  const firedRef = useRef<Set<string>>(new Set());
  const stepMountedAtRef = useRef<number>(Date.now());
  // Latched at mount once status is loaded: was the flow already complete
  // AND naturally so (both saves real) when the user landed here? This is
  // the refresh-after-success replay case. Skipped users have completed_at
  // set without real saves — we don't replay SuccessAnimation for them
  // (separate guard below redirects them out entirely).
  const initiallyCompletedRef = useRef<boolean | null>(null);
  if (initiallyCompletedRef.current === null && !statusLoading) {
    initiallyCompletedRef.current = !!(
      status?.completed_at && status?.page1_saved && status?.page2_saved
    );
  }

  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const modLabel = isMac ? '⌘' : 'Ctrl';

  // Step 4 viewed once on first non-loading render.
  useEffect(() => {
    if (statusLoading || !status) return;
    if (firedRef.current.has('viewed')) return;
    firedRef.current.add('viewed');
    analytics.track('onboarding_step_viewed', {
      step_index: 4, step_name: 'welcome_save',
    });
  }, [statusLoading, status]);

  // Step 4 completed when page 2 lands on the server.
  useEffect(() => {
    if (!status?.page2_saved) return;
    if (firedRef.current.has('completed_step')) return;
    firedRef.current.add('completed_step');
    analytics.track('onboarding_step_completed', {
      step_index: 4, step_name: 'welcome_save',
      duration_ms: Math.max(0, Date.now() - stepMountedAtRef.current),
    });
  }, [status?.page2_saved]);

  // Full funnel completed: total_duration_ms from server-side started_at,
  // which gives a clean cross-session measurement even after refreshes.
  //
  // Gated on REAL completion (both saves landed), not just completed_at —
  // skip path also sets completed_at without performing the saves, and we
  // already fire `onboarding_skipped` on that path. Mixing the two would
  // inflate the conversion rate in PostHog dashboards.
  useEffect(() => {
    if (!status?.completed_at) return;
    if (!status?.page1_saved || !status?.page2_saved) return;
    if (firedRef.current.has('completed_funnel')) return;
    firedRef.current.add('completed_funnel');
    const startedAt = status.started_at ? new Date(status.started_at).getTime() : null;
    const totalDurationMs = startedAt ? Math.max(0, Date.now() - startedAt) : undefined;
    analytics.track('onboarding_completed', {
      total_duration_ms: totalDurationMs,
      device: 'desktop',
    });
  }, [status?.completed_at, status?.started_at, status?.page1_saved, status?.page2_saved]);

  // Floating "Add a note, then hit Save" hint, gated on chord-observed +
  // 2.5s lag. The longer wait gives the extension's note-input toast time
  // to settle into the user's attention before we add a second message —
  // if the hint races the toast, both fight for focus and neither lands.
  // Sticky once shown: stays visible even if ChordProgress reverts to
  // idle (which it does after ~12s with no save) — the user is likely
  // sitting in the toast typing.
  useEffect(() => {
    if (welcomePhase === 'save_confirmed' || welcomePhase === 'done') {
      setNoteHintVisible(false);
      return;
    }
    if (noteHintShownRef.current) return;
    if (welcomePhase !== 'modifiers_held') return;
    const t = setTimeout(() => {
      setNoteHintVisible(true);
      noteHintShownRef.current = true;
    }, 2500);
    return () => clearTimeout(t);
  }, [welcomePhase]);

  // Poll for the page-2 save (drives ChordProgress.saved via status.page2_saved).
  // 400ms burst once ChordProgress reports modifiers_held; 3s otherwise.
  useEffect(() => {
    if (!user) return;
    if (welcomePhase === 'done') return;
    if (status?.completed_at) return;

    const intervalMs = welcomePhase === 'modifiers_held' ? 400 : 3000;
    const tick = () => {
      api<{ step: number; completed: boolean; page2_saved: boolean }>(
        '/api/onboarding/advance',
        { method: 'POST', body: JSON.stringify({}) },
      )
        .then((res) => {
          if (res.completed || res.page2_saved) refetch();
        })
        .catch(() => { /* swallow */ });
    };
    const interval = setInterval(tick, intervalMs);
    return () => clearInterval(interval);
  }, [user, welcomePhase, status?.completed_at, refetch]);

  // Live ⌘⇧S chord feedback + eager poll
  useEffect(() => {
    const isModKey = (k: string) => (isMac ? k === 'Meta' : k === 'Control');
    const down = (ev: KeyboardEvent) => {
      if (isModKey(ev.key)) setModDown(true);
      if (ev.key === 'Shift') setShiftDown(true);
      if (ev.code === 'KeyS') setSDown(true);
    };
    const up = (ev: KeyboardEvent) => {
      if (isModKey(ev.key)) setModDown(false);
      if (ev.key === 'Shift') setShiftDown(false);
      if (ev.code === 'KeyS') setSDown(false);
    };
    const clear = () => { setModDown(false); setShiftDown(false); setSDown(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, [isMac]);

  // (Eager /advance poll on chord-fire is now owned by ChordProgress via
  //  the onFire callback — see the ChordProgress mount below.)

  // After the success animation runs, redirect to wherever the user was
  // originally trying to go (e.g. /upgrade?from=email_trial_d11 — preserved
  // by ProtectedRoute on the bounce to /onboarding). Falls back to /saved.
  //
  // useCallback so SuccessAnimation's effect — which depends on this — does
  // not re-run on every parent render. Without this, the animation timers
  // get torn down and re-armed each tick, causing visible jitter and
  // (rarely) double redirects.
  const handleAnimationDone = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    const dest = consumePendingDestination() || '/saved';
    window.location.replace(dest);
  }, []);

  // Hard-skip from the page-2 chord. Same contract as OnboardingStart:
  // analytics + navigation only fire after a confirmed server skip.
  // The in-flight ref suppresses double-click double-emission.
  const skipInFlightRef = useRef(false);
  const handleSkipWelcome = useCallback(async () => {
    if (navigatedRef.current) return;
    if (skipInFlightRef.current) return;
    skipInFlightRef.current = true;
    try {
      await api('/api/onboarding/skip', {
        method: 'POST',
        body: JSON.stringify({ skipped_at_step: 4, reason: 'shortcut_did_not_fire' }),
      });
    } catch (err) {
      console.error('Failed to mark onboarding skipped:', err);
      // Stay on the page — user can try again. No analytics emitted.
      skipInFlightRef.current = false;
      return;
    }
    analytics.track('onboarding_skipped', {
      skipped_at_step: 4,
      step_name: 'welcome_save',
      reason: 'shortcut_did_not_fire',
    });
    navigatedRef.current = true;
    const dest = consumePendingDestination() || '/saved';
    window.location.replace(dest);
  }, []);

  if (isAuthLoading || statusLoading) {
    return <RouteLoader />;
  }

  // Defensive: if user lands here but page 1 isn't done yet, send them back
  if (status && !status.page1_saved && !status.completed_at) {
    return <Navigate to="/onboarding" replace />;
  }

  // Skipped user manually re-visiting /onboarding/welcome — they're done,
  // but didn't earn the celebration. Drop them in /saved.
  if (
    status?.completed_at &&
    (!status.page1_saved || !status.page2_saved)
  ) {
    return <Navigate to="/saved" replace />;
  }

  // ChordProgress reports done (after the ~800ms success bar) → play
  // success animation. initiallyCompletedRef handles the refresh-after-
  // success replay only.
  if (welcomePhase === 'done' || initiallyCompletedRef.current === true) {
    return <SuccessAnimation onComplete={handleAnimationDone} />;
  }

  const usingBuiltInAI = status?.mcp_setup_status === 'no_compatible_ai';

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>Welcome to Solem</title>
      </Helmet>

      <div className="mx-auto max-w-2xl px-6 pt-20 pb-24">
        <header className="mb-10">
          <p className="mb-2 text-sm font-medium uppercase tracking-wider text-neutral-400">
            One last thing
          </p>
          <h1 className="text-2xl font-light tracking-tight text-neutral-900">
            🎉 You're set up.
          </h1>
          <p className="mt-2 text-base font-light text-neutral-600/80">
            Two things that make Solem brilliant — then save this page to finish.
          </p>
        </header>

        <ul className="mb-12 space-y-5">
          <Tip
            Icon={Devices}
            title="Available anywhere you sign in"
            body="Sign in on any device and your saves come with you."
          />
          {usingBuiltInAI ? (
            <Tip
              Icon={ChatCircle}
              title="Ask Solem about your saves anytime"
              body="Solem's built-in AI is ready in Chat — no setup needed."
            />
          ) : (
            <Tip
              Icon={Sparkle}
              title="AI that knows what you know"
              body="Ask Claude, Cursor or any AI — they answer from your saves."
            />
          )}
        </ul>

        <div className="rounded-sm border border-neutral-200 bg-neutral-50 p-6">
          <h2 className="text-base font-medium text-neutral-900">
            Save this page
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            Press the shortcut below. We'll show a confirmation as soon as the
            save lands.
          </p>
          <div className="mt-5">
            <ChordProgress
              keys={[modLabel, '⇧', 'S']}
              pressed={[modDown, shiftDown, sDown]}
              saved={!!status?.page2_saved}
              targetKeyName="S"
              onFire={() => {
                // Eager /advance the moment modifiers are observed —
                // pairs with the 400ms burst-poll cadence above.
                api<{ completed: boolean; page2_saved: boolean }>(
                  '/api/onboarding/advance',
                  { method: 'POST', body: JSON.stringify({}) },
                )
                  .then((res) => { if (res.completed || res.page2_saved) refetch(); })
                  .catch(() => { /* swallow */ });
              }}
              onPhaseChange={setWelcomePhase}
              onSkip={handleSkipWelcome}
              idleHint="Press the chord above to save this page."
            />
          </div>
        </div>
      </div>

      {/* Floating guidance. Sits in the empty space below the "Save this
          page" card — ~33% of the way up the viewport — close enough to
          the card that the user's eyes catch it without scanning, far
          enough not to crowd it. Appears 2.5s after the chord is
          observed; sticky until the save lands. Cubic-bezier ease
          matches the floating-navbar curve used elsewhere in the app for
          a coherent "soft arrival" feel. rounded-sm pairs the pill with
          the card above it (single radius system across the app). */}
      <div
        aria-hidden={!noteHintVisible}
        className={cn(
          'pointer-events-none fixed inset-x-0 bottom-[28%] z-30 flex justify-center px-6 transition-all duration-700',
          'ease-[cubic-bezier(0.16,1,0.3,1)]',
          noteHintVisible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-3 scale-[0.97] opacity-0',
        )}
      >
        <p className="rounded-sm bg-white/90 px-4 py-2 text-sm text-neutral-600 shadow-sm ring-1 ring-neutral-200 backdrop-blur-sm">
          Add a note, then hit{' '}
          <span className="font-medium text-neutral-900">Save</span>.
        </p>
      </div>
    </main>
  );
}

interface TipProps {
  Icon: React.ComponentType<{ size?: number; weight?: 'bold' | 'regular' | 'light' }>;
  title: string;
  body: string;
}

function Tip({ Icon, title, body }: TipProps) {
  return (
    <li className="flex gap-4">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-neutral-200 bg-white text-neutral-700">
        <Icon size={18} weight="regular" />
      </div>
      <div className="flex-1">
        <h3 className="text-base font-medium text-neutral-900">{title}</h3>
        <p className="mt-0.5 text-sm leading-snug text-neutral-600">{body}</p>
      </div>
    </li>
  );
}
